
import React, { useState, useEffect } from 'react'
import { Row, Col, Container, Button, Badge, Card as BootstrapCard, Modal, Form } from 'react-bootstrap'
import Card from '../../../components/Card'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { 
  fetchDailyReports,
  selectReportsList,
  selectLoading,
  selectError,
  selectMeta,
  clearErrors,
  setSelectedReport,
  selectSelectedReport,
  clearSelectedReport
} from '../../../store/dailyReport/dailyReportSlice';

import LoaderModal from '../../../components/loaderModal';
import NoDataFound from '../../../components/noDataFound';

//Sweet alert
import Swal from 'sweetalert2'
import DOMPurify from 'isomorphic-dompurify';

const DailyReports = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Redux state
  const reportsList = useSelector(selectReportsList);
  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);
  const meta = useSelector(selectMeta);
  const selectedReport = useSelector(selectSelectedReport);

  const [page, setPage] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [isFirst, setIsFirst] = useState(true);
  
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
    setIsFirst(false)
  }, [isFirst]);

  // Load daily reports on component mount
  useEffect(() => {
    if (localStorage.getItem("uzoneAuthToken")) {
      dispatch(fetchDailyReports({ page: 1, limit: 10 }));
    } else {
      navigate('/')
    }
  }, [dispatch, navigate]);

  // Handle API errors
  useEffect(() => {
    if (error) {
      Swal.fire({
        title: 'Error',
        text: error,
        icon: 'error',
        confirmButtonText: 'Okay'
      }).then(() => {
        dispatch(clearErrors());
      });
    }
  }, [error, dispatch]);

  // Load more data
  const loadMore = () => {
    if (page < meta.last_page) {
      const nextPage = page + 1;
      dispatch(fetchDailyReports({
        page: nextPage,
        limit: 10,
        startDate: startDate || null,
        endDate: endDate || null,
      }));
      setPage(nextPage);
    }
  };

  // Handle date filter
  const handleDateFilter = () => {
    if (startDate && endDate && startDate > endDate) {
      Swal.fire({
        title: 'Invalid Date Range',
        text: 'Start date cannot be after end date.',
        icon: 'warning',
        confirmButtonText: 'Okay'
      });
      return;
    }

    setPage(1);
    dispatch(fetchDailyReports({
      page: 1,
      limit: 10,
      startDate: startDate || null,
      endDate: endDate || null,
    }));
  };

  // Clear date filter
  const clearFilter = () => {
    setStartDate('');
    setEndDate('');
    setPage(1);
    dispatch(fetchDailyReports({ page: 1, limit: 10 }));
  };

  // Format date helper function
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  // Format time helper function
  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    const [hours, minutes] = timeString.split(':');
    const hour24 = parseInt(hours);
    const min = parseInt(minutes);
    
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
    const ampm = hour24 >= 12 ? 'PM' : 'AM';
    const formattedMinutes = min.toString().padStart(2, '0');
    
    return `${hour12}:${formattedMinutes} ${ampm}`;
  };

  // Get bring more icon
  const getBringMoreIcon = (thingId) => {
    const icons = {
      1: { icon: 'ri-parent-line', label: 'Diapers', color: 'primary' },
      2: { icon: 'ri-file-paper-line', label: 'Wipes', color: 'info' },
      3: { icon: 'ri-medicine-bottle-line', label: 'Ointment', color: 'success' },
      4: { icon: 'ri-shirt-line', label: 'Clothes', color: 'warning' },
      5: { icon: 'ri-hotel-bed-line', label: 'Blanket', color: 'danger' },
      6: { icon: 'ri-more-2-line', label: 'Other', color: 'secondary' }
    };
    return icons[thingId] || icons[6];
  };

  // Get diaper check label
  const getDiaperCheckLabel = (checkType) => {
    const labels = {
      1: 'd/t',
      2: 'Dry',
      3: 'Wet',
      4: 'BM'
    };
    return labels[checkType] || 'Unknown';
  };

  // Get meal amount label
  const getMealAmountLabel = (amount) => {
    const labels = {
      1: 'All',
      2: 'Some',
      3: 'None'
    };
    return labels[amount] || 'Unknown';
  };

  // Handle view details
  const handleViewDetails = (report) => {
    dispatch(setSelectedReport(report));
    setShowDetailsModal(true);
  };

  // Handle close modal
  const handleCloseModal = () => {
    setShowDetailsModal(false);
    dispatch(clearSelectedReport());
  };

  return (
    <>
      <LoaderModal showLoader={loading && page === 1} />
      <div id='content-page' className='content-page'>
        <Container>
          <Row>
            <Col sm="12">
              <Card>
                <Card.Header>
                  <div className="d-flex justify-content-between align-items-center flex-wrap">
                    <div className="header-title mb-2 mb-md-0">
                      <h4 className="card-title">
                        <i className="ri-file-list-3-line me-2"></i>
                        Daily Reports
                      </h4>
                      <p className="text-muted mb-0">
                        View your child's daily activity reports
                      </p>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body>
                  {/* Date Range Filter */}
                  <Row className="mb-4">
                    <Col md={10} lg={9}>
                      <Form.Group>
                        <Form.Label>Filter by Date Range</Form.Label>
                        {/* Used to be one flex row that fell back to wrapping
                            once the two date fields' min-width didn't fit —
                            on a real phone that threshold landed mid-layout,
                            leaving the filter button stranded on its own line,
                            left-aligned under a lot of empty space instead of
                            looking deliberate. True grid columns instead: the
                            two dates always split the row 50/50 (xs={6}) down
                            to any width, and the buttons get their own full
                            row (xs={12}) rather than an accidental leftover
                            one. md+ pulls the buttons back inline after the
                            dates, matching the original single-row filter. */}
                        <Row className="g-2 align-items-end">
                          <Col xs={6} md={4} lg={3}>
                            <Form.Label className="small text-muted mb-1">Start Date</Form.Label>
                            <Form.Control
                              type="date"
                              value={startDate}
                              onChange={(e) => setStartDate(e.target.value)}
                            />
                          </Col>
                          <Col xs={6} md={4} lg={3}>
                            <Form.Label className="small text-muted mb-1">End Date</Form.Label>
                            <Form.Control
                              type="date"
                              value={endDate}
                              min={startDate || undefined}
                              onChange={(e) => setEndDate(e.target.value)}
                            />
                          </Col>
                          <Col xs={12} md="auto" className="d-flex gap-2">
                            <Button
                              variant="primary"
                              onClick={handleDateFilter}
                              disabled={loading}
                              className="flex-fill flex-md-grow-0"
                            >
                              <i className="ri-search-line"></i>
                            </Button>
                            {(startDate || endDate) && (
                              <Button
                                variant="outline-secondary"
                                onClick={clearFilter}
                                disabled={loading}
                                className="flex-fill flex-md-grow-0"
                              >
                                <i className="ri-close-line"></i>
                              </Button>
                            )}
                          </Col>
                        </Row>
                      </Form.Group>
                    </Col>
                  </Row>

                  {reportsList?.length > 0 ? (
                    <>
                      <Row>
                        {reportsList.map((report) => (
                          <Col md={12} key={report.id} className="mb-3">
                            <BootstrapCard 
                              className="shadow-sm cursor-pointer hover-shadow"
                              onClick={() => handleViewDetails(report)}
                              style={{ cursor: 'pointer', transition: 'all 0.3s ease' }}
                            >
                              <BootstrapCard.Body>
                                <Row className="align-items-center">
                                  <Col md={8}>
                                    <div className="d-flex align-items-center mb-2">
                                      <i className="ri-calendar-line fs-4 text-primary me-2"></i>
                                      <h5 className="mb-0">{formatDate(report.report_date)}</h5>
                                    </div>
                                    {report.learned && (
                                      <p className="mb-1 text-muted">
                                        <strong>What they learned:</strong> {report.learned.substring(0, 100)}{report.learned.length > 100 ? '...' : ''}
                                      </p>
                                    )}
                                    <div className="d-flex gap-2 flex-wrap mt-2">
                                      {report.daily_report_meals?.length > 0 && (
                                        <Badge bg="success">
                                          <i className="ri-restaurant-line me-1"></i>
                                          {report.daily_report_meals.length} Meals
                                        </Badge>
                                      )}
                                      {report.daily_report_sleeps?.length > 0 && (
                                        <Badge bg="info">
                                          <i className="ri-zzz-line me-1"></i>
                                          {report.daily_report_sleeps.length} Naps
                                        </Badge>
                                      )}
                                      {report.daily_report_diapers?.length > 0 && (
                                        <Badge bg="warning">
                                          <i className="ri-parent-line me-1"></i>
                                          {report.daily_report_diapers.length} Diaper Changes
                                        </Badge>
                                      )}
                                      {report.daily_report_fealings?.length > 0 && (
                                        // Bootstrap badges are `white-space: nowrap` by default — fine for
                                        // the single-word case, but this one joins every feeling into one
                                        // badge, and on a narrow phone width that joined string doesn't
                                        // wrap, forcing the badge (and the card under it) wider than the
                                        // viewport instead of just taking a second line.
                                        <Badge bg="primary" style={{ whiteSpace: 'normal', textAlign: 'left' }}>
                                          <i className="ri-emotion-happy-line me-1"></i>
                                          {report.daily_report_fealings.map(f => f.fealing?.name).join(', ')}
                                        </Badge>
                                      )}
                                    </div>
                                  </Col>
                                  <Col md={4} className="text-md-end mt-3 mt-md-0 d-grid d-md-block">
                                    {/* Purely decorative — the whole Card above already handles the
                                        click via handleViewDetails. Left un-blurred, a mouse click
                                        leaves this button sitting in the browser's native :focus
                                        state, which Bootstrap's outline-primary style renders as a
                                        filled background — so an already-clicked report's button
                                        stayed visibly darker than one that hadn't been clicked yet.
                                        `d-grid`/`d-md-block` makes it full-width under the stacked
                                        content on mobile instead of a small button orphaned on the
                                        right, while staying its normal auto-width, right-aligned
                                        self on md+ where the two columns sit side by side. */}
                                    <Button
                                      variant="outline-primary"
                                      onClick={(e) => e.currentTarget.blur()}
                                    >
                                      View Details
                                      <i className="ri-arrow-right-line ms-2"></i>
                                    </Button>
                                  </Col>
                                </Row>
                              </BootstrapCard.Body>
                            </BootstrapCard>
                          </Col>
                        ))}
                      </Row>
                      
                      {/* Load More Button */}
                      {page < meta.last_page && (
                        <div className="text-center mt-3">
                          <Button 
                            variant="outline-primary" 
                            onClick={loadMore}
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status">
                                  <span className="visually-hidden">Loading...</span>
                                </span>
                                Loading...
                              </>
                            ) : (
                              <>
                                <i className="ri-arrow-down-line me-1"></i>
                                Load More
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </>
                  ) : (
                    <NoDataFound />
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Details Modal */}
      <Modal 
        show={showDetailsModal} 
        onHide={handleCloseModal} 
        size="lg" 
        scrollable
        centered
        style={{ zIndex: 9999 }}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="ri-file-list-3-line me-2"></i>
            Daily Report Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
          {selectedReport && (
            <>
              <h5 className="text-primary mb-3">
                <i className="ri-calendar-line me-2"></i>
                {formatDate(selectedReport.report_date)}
              </h5>

              {/* What They Learned */}
              {selectedReport.learned && (
                <div className="mb-4">
                  <h6 className="text-success">
                    <i className="ri-book-open-line me-2"></i>
                    What They Learned:
                  </h6>
                  {/* Saved as HTML (rich text / AI-generated content entered by
                      staff), so render it rather than printing the tags as
                      literal text - a plain <p> here can't hold it since the
                      value often already contains its own <p> tags. */}
                  <div className="ms-4" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedReport.learned) }} />
                </div>
              )}

              {/* Feelings */}
              {selectedReport.daily_report_fealings?.length > 0 && (
                <div className="mb-4">
                  <h6 className="text-primary">
                    <i className="ri-emotion-line me-2"></i>
                    How They Felt:
                  </h6>
                  <div className="ms-4">
                    {selectedReport.daily_report_fealings.map((feeling, index) => (
                      <Badge key={index} bg="primary" className="me-2">
                        <i className="ri-emotion-happy-line me-1"></i>
                        {feeling.fealing?.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Meals */}
              {selectedReport.daily_report_meals?.length > 0 && (
                <div className="mb-4">
                  <h6 className="text-success">
                    <i className="ri-restaurant-line me-2"></i>
                    Meals:
                  </h6>
                  <div className="ms-4 table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Time</th>
                          <th>Type</th>
                          <th>Amount Eaten</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedReport.daily_report_meals.map((meal, index) => (
                          <tr key={index}>
                            <td>{formatTime(meal.meal_time)}</td>
                            <td>{meal.menu_type?.name}</td>
                            <td>
                              <Badge bg={meal.meal_amount === 1 ? 'success' : meal.meal_amount === 2 ? 'warning' : 'danger'}>
                                {getMealAmountLabel(meal.meal_amount)}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Sleep Times */}
              {selectedReport.daily_report_sleeps?.length > 0 && (
                <div className="mb-4">
                  <h6 className="text-info">
                    <i className="ri-zzz-line me-2"></i>
                    Nap Times:
                  </h6>
                  <div className="ms-4">
                    {selectedReport.daily_report_sleeps.map((sleep, index) => (
                      <div key={index} className="mb-2">
                        <Badge bg="info">
                          {formatTime(sleep.start_time)} - {formatTime(sleep.end_time)}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Diaper Changes */}
              {selectedReport.daily_report_diapers?.length > 0 && (
                <div className="mb-4">
                  <h6 className="text-warning">
                    <i className="ri-parent-line me-2"></i>
                    Diaper Changes:
                  </h6>
                  <div className="ms-4 table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Time</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedReport.daily_report_diapers.map((diaper, index) => (
                          <tr key={index}>
                            <td>{formatTime(diaper.diaper_time)}</td>
                            <td>
                              <Badge bg="warning">{getDiaperCheckLabel(diaper.diaper_check)}</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Bring More Items */}
              {selectedReport.daily_report_bring_mores?.length > 0 && (
                <div className="mb-4">
                  <h6 className="text-danger">
                    <i className="ri-shopping-bag-line me-2"></i>
                    Please Bring More:
                  </h6>
                  <div className="ms-4">
                    <Row>
                      {selectedReport.daily_report_bring_mores.map((item, index) => {
                        const iconInfo = getBringMoreIcon(item.things);
                        return (
                          <Col md={4} key={index} className="mb-2">
                            <div className={`alert alert-${iconInfo.color} py-2 px-3 mb-0`}>
                              <i className={`${iconInfo.icon} me-2`}></i>
                              <strong>{item.other || iconInfo.label}</strong>
                            </div>
                          </Col>
                        );
                      })}
                    </Row>
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedReport.notes && (
                <div className="mb-4">
                  <h6 className="text-info">
                    <i className="ri-sticky-note-line me-2"></i>
                    Notes:
                  </h6>
                  <p className="ms-4">{selectedReport.notes}</p>
                </div>
              )}

              {/* Comments */}
              {selectedReport.comments && (
                <div className="mb-4">
                  <h6 className="text-secondary">
                    <i className="ri-chat-3-line me-2"></i>
                    Teacher's Comments:
                  </h6>
                  {/* Same as "What They Learned" above - stored as HTML, so it
                      needs to be rendered, not printed as literal tag text. */}
                  <div className="ms-4" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedReport.comments) }} />
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default DailyReports
